import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  debounceTime,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import { DialogService } from '~console/app/~shared/services/dialog.service';
import { MapService } from '~console/app/~shared/services/map.service';
import { LoadListener } from '~console/app/~shared/utils/rxjs';
import { removeNilProperties } from '~console/app/~shared/utils/utils';
import {
  coordinateValidator,
  phoneNumberValidator,
  validatePhoneNumber
} from '~console/app/~shared/utils/validators';
import { ReferenceData } from '~core/models/reference-data.models';
import { ReferenceDataService } from '~core/services/reference-data.service';
import { GpsCoordinates } from '../../../~shared/models/shared.models';
import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { OnboardingFormMapDialogComponent } from '../../modals/onboarding-form-map-dialog/onboarding-form-map-dialog.component';

@Component({
  selector: 'onboarding-form',
  standalone: false,
  templateUrl: './onboarding-form.component.html',
  styleUrl: './onboarding-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingFormComponent
  extends BaseAuthComponent
  implements OnInit
{
  private readonly _referenceDataService = inject(ReferenceDataService);
  protected _dialogService = inject(DialogService);
  private readonly _mapService = inject(MapService);

  onboardingForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        phoneNumberValidator({
          country: this._constantsService.defaultCountry
        })
      ]
    }),
    city: new FormControl(null, [Validators.required]),
    district: new FormControl(null, [Validators.required]),
    neighborhood: new FormControl('', [Validators.required]),
    location: new FormControl('', [Validators.required]),
    activity: new FormControl(null, [Validators.required]),
    rccNumber: new FormControl(''),
    section: new FormControl(''),
    parcel: new FormControl(''),
    lot: new FormControl(''),
    locationLink: new FormControl(''),
    ilot: new FormControl(''),
    longitude: new FormControl('', [
      Validators.required,
      coordinateValidator(-9, -2)
    ]),
    latitude: new FormControl('', [
      Validators.required,
      coordinateValidator(4, 11)
    ]),
    siteOwnerName: new FormControl(''),
    siteOwnerPhoneNumber: new FormControl(''),
    siteOwnerNif: new FormControl('')
  });

  isLoggingOut$ = this._authFacade.isLoggingOut$;
  isSubmitting = signal(false);
  validatePhoneNumberFn = validatePhoneNumber;
  currentUser = toSignal(this._authFacade.currentUser$, {
    initialValue: null
  });

  citiesListener: LoadListener<ReferenceData[]> | undefined;
  districtsListener: LoadListener<ReferenceData[]> | undefined;
  activitiesListener: LoadListener<ReferenceData[]> | undefined;

  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.onboarding.locationFormValues`;

  isLocationLinkInvalidFormat = signal(false);
  isLocationLinkGeocodingFailed = signal(false);

  ngOnInit(): void {
    this.initCitiesListener();
    this.initDistrictsListener();
    this.initActivitiesListener();
    this._setupLocationLinkSubscription();
  }

  onSubmit(): void {
    if (this.onboardingForm.invalid) {
      this.onboardingForm.markAllAsTouched();
      return;
    }
    this._eventService.emit({ type: 'GO_TO_NEXT_STEP' });
  }

  onLogout(): void {
    this._authFacade.logout();
  }

  openMapDialog(): void {
    const dialogRef = this._dialogService.open({
      component: OnboardingFormMapDialogComponent,
      config: {
        panelClass: ['mat-dialog-custom', 'w-[800px]'],
        data: {
          lat: parseFloat(this.latitudeCtrl?.value),
          lng: parseFloat(this.longitudeCtrl?.value)
        },
        disableClose: false,
        maxWidth: '800px'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map((result: GpsCoordinates | null) => {
          if (result) {
            this._patchCoordinates(result);
          }
        })
      )
      .subscribe();
  }

  initCitiesListener(): void {
    const cities$ = this._referenceDataService
      .list({
        type: 'ref_city',
        itemPerPage: 100_000_000
      })
      .pipe(map(res => res.items));
    this.citiesListener = new LoadListener(cities$);
  }

  initDistrictsListener(parentValue?: string): void {
    const params = removeNilProperties({
      type: 'ref_district',
      parentValue: parentValue,
      itemPerPage: 100_000_000
    });
    const districts$ = parentValue
      ? this._referenceDataService.list(params).pipe(map(res => res.items))
      : of([]);
    this.districtsListener = new LoadListener(districts$);
  }

  initActivitiesListener(): void {
    const activities$ = this._referenceDataService
      .list({
        type: 'nature_of_activity',
        itemPerPage: 100_000_000
      })
      .pipe(map(res => res.items));
    this.activitiesListener = new LoadListener(activities$);
  }

  getControlField(field: string): AbstractControl | null {
    return this.onboardingForm.get(field);
  }

  get longitudeCtrl(): AbstractControl | null {
    return this.onboardingForm.get('longitude');
  }

  get latitudeCtrl(): AbstractControl | null {
    return this.onboardingForm.get('latitude');
  }

  private _setupLocationLinkSubscription(): void {
    const locationLinkControl = this.onboardingForm.get('locationLink');
    if (!locationLinkControl) return;

    locationLinkControl.valueChanges
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        debounceTime(500),
        filter((url): url is string => !!url && url.trim().length > 0),
        tap(url => this._handleGoogleMapsUrl(url))
      )
      .subscribe();
  }

  private _extractCoordinatesFromUrl(
    url: string
  ): { lat: number; lng: number } | null {
    const trimmedUrl = url.trim();

    const rawMatch = trimmedUrl.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (rawMatch) {
      const lat = parseFloat(rawMatch[1]);
      const lng = parseFloat(rawMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }

    try {
      const urlObj = new URL(trimmedUrl);

      if (
        urlObj.hostname.includes('google.com') &&
        urlObj.pathname.startsWith('/maps/@')
      ) {
        const match = urlObj.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (match) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
        }
      }

      if (urlObj.searchParams.has('q')) {
        const q = urlObj.searchParams.get('q') || '';
        const coordsMatch = q.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)/);
        if (coordsMatch) {
          const lat = parseFloat(coordsMatch[1]);
          const lng = parseFloat(coordsMatch[2]);
          if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
        }
      }

      if (urlObj.searchParams.has('ll')) {
        const ll = urlObj.searchParams.get('ll') || '';
        const [latStr, lngStr] = ll.split(',');
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
      }

      return null;
    } catch {
      return null;
    }
  }

  private _isGoogleMapsUrl(url: string): boolean {
    try {
      const u = new URL(url.trim());
      return (
        u.hostname.endsWith('google.com') ||
        u.hostname.endsWith('google.fr') ||
        u.hostname.endsWith('google.co.uk') ||
        u.hostname === 'maps.app.goo.gl' ||
        u.hostname === 'goo.gl'
      );
    } catch {
      return false;
    }
  }

  private _handleGoogleMapsUrl(url: string): void {
    this.isLocationLinkInvalidFormat.set(false);
    this.isLocationLinkGeocodingFailed.set(false);

    const coords = this._extractCoordinatesFromUrl(url);
    if (coords) {
      this._patchCoordinates(coords);
      return;
    }

    if (!this._isGoogleMapsUrl(url)) {
      this.isLocationLinkInvalidFormat.set(true);
      return;
    }

    this._geocodeAddress(url)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: coords => this._patchCoordinates(coords),
          error: () => this.isLocationLinkGeocodingFailed.set(true)
        })
      )
      .subscribe();
  }

  private _geocodeAddress(
    address: string
  ): Observable<{ lat: number; lng: number }> {
    return this._mapService.getGeocodingLibrary().pipe(
      takeUntilDestroyed(this._destroyRef),
      switchMap(geocodingLib => {
        const geocoder = new geocodingLib.Geocoder();
        return from(geocoder.geocode({ address }));
      }),
      map(result => {
        if (result.results.length > 0) {
          const location = result.results[0].geometry.location;
          return { lat: location.lat(), lng: location.lng() };
        }
        throw new Error('No results');
      })
    );
  }

  private _patchCoordinates(coords: GpsCoordinates): void {
    this.onboardingForm.patchValue({
      latitude: coords.lat.toString(),
      longitude: coords.lng.toString()
    });
  }
}
